import React, { useEffect } from 'react';
import MaterialTable, { MTableToolbar } from '@material-table/core';
import { connect } from 'react-redux';
import lodash from 'lodash';
import { ThemeProvider } from '@material-ui/styles';
import { theme } from 'common/util/materialTable/theme';
import { localizationBuilder } from 'common/util/materialTable/localization';
import { tableIcons } from 'common/util/materialTable/materialTableIcons';
import { loadUserBinders, resetUserBinders, setRowChecked, setSelectedBinders } from 'part11/actions/userBulkAdd';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import styles from './AddUserToBinderContainer.scss';
import { DEFAULT_DEBOUNCE_INTERVAL } from 'pages/report/utils/constants';
import { colorBlue1, colorGrey4 } from 'styles/_colors.scss';

export const rowMatchesAllSearchTerms = (searchString, rowData) => {
    // material-table-core already trims searchString for us
    const normalizedSearchTerms = searchString.toLowerCase().split(' ');
    const searchableColumns = columns.map((c) => c.field);
    const columnData = lodash.pickBy(rowData, (value, key) => searchableColumns.includes(key));

    const rowMatches = normalizedSearchTerms.every((term) => {
        const matchingFields = lodash.filter(columnData, (fieldValue) => {
            if (fieldValue) {
                const normalizedFieldValue = fieldValue.toLowerCase();
                return normalizedFieldValue.includes(term);
            }
            return false;
        });

        const rowMatches = !lodash.isEmpty(matchingFields);
        return rowMatches;
    });

    return rowMatches;
};

const caseInsensitiveSort = (a, b) => {
    // null or empty values sorted last, regardless of sort direction
    if (!a || a === '') {
        return -1;
    }

    return a.localeCompare(b);
};

const columns = [
    {
        title: 'Name',
        field: 'name',
        width: '50% !important',
        customFilterAndSearch: rowMatchesAllSearchTerms,
        customSort: (a, b) => caseInsensitiveSort(a.name, b.name),
        defaultSort: 'asc',
    },
    {
        title: 'PI',
        field: 'principalInvestigator',
        emptyValue: <p className={styles.emptyLabel}>-</p>,
        width: '25% !important',
        customSort: (a, b) => caseInsensitiveSort(a.principalInvestigator, b.principalInvestigator),
    },
    {
        title: 'Site',
        field: 'site',
        emptyValue: <p className={styles.emptyLabel}>-</p>,
        width: '25% !important',
        customSort: (a, b) => caseInsensitiveSort(a.site, b.site),
    },
];

export const customTheme = {
    ...theme,
    overrides: {
        MuiTableBody: {
            root: {
                outline: 'auto',
            },
        },
        MuiTableCell: {
            root: {
                border: 'none !important',
            },
        },
        MuiSvgIcon: {
            root: {
                color: colorBlue1,
            },
        },
    },
};

const EmptyRecordComponent = () => <h1>No records to display</h1>;

const LoadingDataSpinner = () => {
    return (
        <div>
            <LoadingStatus actionLabel={'loading'} itemName={''} loading={true} />
        </div>
    );
};

export const customLocalization = (isLoading) => {
    const localization = {
        toolbar: {
            searchPlaceholder: 'Search All Binder Details',
            searchTooltip: 'Binders matching search terms are displayed',
        },
        body: {
            emptyDataSourceMessage: isLoading ? <LoadingDataSpinner /> : <EmptyRecordComponent />,
        },
    };
    return localizationBuilder(localization);
};

const AddUserToBinderContainer = (props) => {
    useEffect(() => {
        const { institutionId, loadUserBinders, targetUserId } = props;
        loadUserBinders(institutionId, targetUserId);
        return () => {
            props.resetUserBinders();
        };
    }, []);

    const onRowChecked = (_, data) => {
        props.setRowChecked(data);
    };

    const onSelectionChange = (selectedItems = []) => {
        const selectedBinderIds = selectedItems.map((item) => item.id);
        props.setSelectedBinders(selectedBinderIds);
    };

    const customOptions = (dataLength) => ({
        filtering: false,
        search: true,
        selection: !!dataLength,
        debounceInterval: DEFAULT_DEBOUNCE_INTERVAL,
        showTextRowsSelected: false,
        paging: false,
        showTitle: false,
        emptyRowsWhenPaging: false,
        searchFieldAlignment: 'left',
        maxBodyHeight: '300px',
        thirdSortClick: false,
        searchFieldStyle: {
            backgroundColor: colorGrey4,
            marginTop: '1.5rem',
            padding: '5px 2px',
            color: colorBlue1,
            width: '400px',
            fontSize: '16px !important',
            borderBottom: 'none !important',
            borderRadius: '3px',
        },
    });

    return (
        <ThemeProvider theme={customTheme}>
            <div className={styles.modalContainer} data-cy="AddUserToBinderContainer">
                <MaterialTable
                    icons={tableIcons}
                    options={customOptions(props.binders.length)}
                    localization={customLocalization(props.isLoading)}
                    columns={columns}
                    onRowClick={onRowChecked}
                    data={props.binders}
                    onSelectionChange={onSelectionChange}
                    components={{
                        Toolbar: (binderRow) => (
                            <div>
                                <MTableToolbar {...binderRow} />
                            </div>
                        ),
                    }}
                />
            </div>
        </ThemeProvider>
    );
};

export default connect(
    (state, ownProps) => {
        const binderData = lodash.get(state, 'userBinders', {});
        const isLoading = lodash.get(binderData, 'isLoading', true);
        const binders = lodash.get(binderData, 'allData', []);
        const bindersSortedByName = lodash.sortBy(binders, (binder) => binder.name.toLocaleLowerCase());
        const institutionId = lodash.get(state, 'me.institution.id', false);
        const targetUserId = ownProps.user ? ownProps.user.id : null;
        return {
            isLoading,
            binders: bindersSortedByName,
            institutionId,
            targetUserId,
        };
    },
    {
        loadUserBinders,
        resetUserBinders,
        setRowChecked,
        setSelectedBinders,
    }
)(AddUserToBinderContainer);
